import React, { useEffect } from "react";
import { motion, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";
import "bulma/css/bulma.min.css";
import { InfoService } from "../../../services/site/InfoService";
import VideoPlayer from "../../../Components/VideoPlayer";
import onboardingVideo from "../../../assets/videos/employee.mp4";

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 60 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const fadeInLeft = {
  hidden: { opacity: 0, x: -60 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.6 } },
};

const fadeInRight = {
  hidden: { opacity: 0, x: 60 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.6 } },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 },
  },
};

const scaleIn = {
  hidden: { scale: 0.8, opacity: 0 },
  visible: { scale: 1, opacity: 1, transition: { duration: 0.5 } },
};

// Animated section wrapper
const AnimatedSection: React.FC<{
  children: React.ReactNode;
  className?: string;
  variant?: any;
}> = ({ children, className = "", variant = fadeInUp }) => {
  const controls = useAnimation();
  const [ref, inView] = useInView({ threshold: 0.1, triggerOnce: true });

  useEffect(() => {
    if (inView) {
      controls.start("visible");
    }
  }, [controls, inView]);

  return (
    <motion.section
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={variant}
      className={className}
    >
      {children}
    </motion.section>
  );
};

// Hero Section
const HeroSection: React.FC = () => {
  return (
    <AnimatedSection className="sds_eo-hero section">
      <div className="container">
        <div className="columns is-vcentered">
          <div className="column is-6">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="title is-1 sds_eo-hero-title">
                Employee Onboarding Solution
              </h1>
              <h2 className="subtitle is-4 sds_eo-hero-subtitle mb-5">
                Transform New Hire Onboarding with AI-Powered Automation
              </h2>
              <div className="content is-size-5 sds_eo-hero-description">
                <p className="mb-4">
                  <strong>Streamline Your Hiring Process:</strong> Our
                  intelligent employee onboarding platform combines document
                  collection, AI-powered data extraction, and digital signatures
                  to make hiring faster, easier, and completely paperless.
                </p>
                <p className="mb-4">
                  Enter basic employee details, upload required documents, and
                  watch as AI automatically extracts information and fills out
                  templates. Complete onboarding in minutes, not days!
                </p>
              </div>
              <div className="sds_eo-feature-badges">
                <span className="tag is-large sds_eo-badge">
                  👥 Smart Onboarding
                </span>
                <span className="tag is-large sds_eo-badge">🤖 AI-Powered</span>
                <span className="tag is-large sds_eo-badge">
                  ✍️ E-Signatures
                </span>
                <span className="tag is-large sds_eo-badge">
                  🔒 100% Secure
                </span>
              </div>
            </motion.div>
          </div>
          <div className="column is-6">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="sds_eo-hero-visual"
            >
              <div className="sds_eo-animation">
                <motion.div
                  className="sds_eo-icon-large"
                  animate={{
                    rotate: [0, -5, 5, -5, 0],
                    scale: [1, 1.05, 1],
                  }}
                  transition={{ duration: 3, repeat: Infinity, repeatDelay: 2 }}
                >
                  👥
                </motion.div>
                <div className="sds_eo-floating-elements">
                  {["📋", "✅", "🔐", "📄"].map((icon, i) => (
                    <motion.div
                      key={i}
                      className="sds_eo-floating-element"
                      animate={{ y: [-10, 10, -10] }}
                      transition={{
                        duration: 2 + i,
                        repeat: Infinity,
                        ease: "easeInOut",
                      }}
                    >
                      {icon}
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Featured Video - Employee Onboarding Demo */}
        <VideoPlayer
          videoSrc={onboardingVideo}
          title="Employee Onboarding Made Efficient"
          description="Discover how to onboard employees seamlessly with automated data extraction, document collection, and legally compliant digital signatures"
          lightBackground={false}
        />
      </div>
    </AnimatedSection>
  );
};

// How It Works Section
const HowItWorksSection: React.FC = () => {
  const steps = [
    {
      number: 1,
      icon: "📝",
      title: "Enter Basic Details",
      desc: "Fill in essential employee information - name, position, department, start date, and contact details.",
      features: [
        "Simple web form",
        "Auto-save feature",
        "Field validation",
        "Mobile-friendly",
      ],
    },
    {
      number: 2,
      icon: "📄",
      title: "Select & Upload Documents",
      desc: "Choose required documents (ID proof, education certificates, previous employment docs) and upload them.",
      features: [
        "Document checklist",
        "Multiple file formats",
        "Drag & drop upload",
        "Bulk upload support",
      ],
    },
    {
      number: 3,
      icon: "🤖",
      title: "AI Extracts Information",
      desc: "Our AI automatically reads uploaded documents and extracts all relevant information.",
      features: [
        "Smart OCR technology",
        "Multi-language support",
        "99% accuracy rate",
        "Instant processing",
      ],
    },
    {
      number: 4,
      icon: "📋",
      title: "Auto-Fill Templates",
      desc: "AI populates employment agreement, NDA, and other templates with extracted data automatically.",
      features: [
        "Template library",
        "Custom templates",
        "Smart field mapping",
        "Preview before sign",
      ],
    },
    {
      number: 5,
      icon: "✍️",
      title: "Digital Signatures",
      desc: "Employee and HR sign all documents digitally - legally binding and completely paperless.",
      features: [
        "E-signature integration",
        "Multi-party signing",
        "Email notifications",
        "Audit trail",
      ],
    },
    {
      number: 6,
      icon: "💾",
      title: "Export & Archive",
      desc: "Download consolidated employee data with all signed documents, securely stored for future reference.",
      features: [
        "Export to Excel/PDF",
        "Document archive",
        "Secure cloud storage",
        "Easy retrieval",
      ],
    },
  ];

  return (
    <AnimatedSection className="sds_eo-process section sds_eo-bg-light">
      <div className="container">
        <div className="has-text-centered mb-6">
          <h2 className="title is-2 sds_eo-section-title">
            How It Works - Just 6 Simple Steps
          </h2>
          <p className="subtitle is-5">
            Complete employee onboarding in minutes
          </p>
        </div>

        <motion.div
          className="columns is-multiline"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {steps.map((step, idx) => (
            <motion.div key={idx} className="column is-4" variants={fadeInUp}>
              <motion.div
                className="sds_eo-process-card"
                whileHover={{ y: -10 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <div className="sds_eo-step-number">{step.number}</div>
                <div className="sds_eo-step-icon">{step.icon}</div>
                <h4 className="title is-4">{step.title}</h4>
                <p className="is-size-6 has-text-grey mb-4">{step.desc}</p>
                <ul className="sds_eo-feature-list">
                  {step.features.map((feature, fIdx) => (
                    <li key={fIdx}>
                      <span className="icon has-text-success is-small">
                        <i className="fas fa-check"></i>
                      </span>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </AnimatedSection>
  );
};

// Key Features Section
const KeyFeaturesSection: React.FC = () => {
  const features = [
    {
      icon: "📋",
      title: "Document Collection",
      desc: "Automated checklist for required documents based on role and department.",
      color: "#4CAF50",
    },
    {
      icon: "🤖",
      title: "AI Data Extraction",
      desc: "Intelligent extraction of information from resumes, certificates, and IDs.",
      color: "#2196F3",
    },
    {
      icon: "📝",
      title: "Template Management",
      desc: "Pre-built and customizable templates for all onboarding documents.",
      color: "#FF9800",
    },
    {
      icon: "✍️",
      title: "Digital Signatures",
      desc: "Fully integrated e-signature workflows for all parties.",
      color: "#9C27B0",
    },
    {
      icon: "🔒",
      title: "Security & Compliance",
      desc: "Bank-grade encryption with complete audit trails and compliance.",
      color: "#F44336",
    },
    {
      icon: "📊",
      title: "Data Export",
      desc: "Export consolidated data with documents in various formats.",
      color: "#00BCD4",
    },
  ];

  return (
    <AnimatedSection className="sds_eo-features section">
      <div className="container">
        <div className="has-text-centered mb-6">
          <h2 className="title is-2 sds_eo-section-title">
            Powerful Features for Modern HR
          </h2>
          <p className="subtitle is-5">
            Everything you need for seamless employee onboarding
          </p>
        </div>

        <motion.div
          className="columns is-multiline"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          {features.map((feature, index) => (
            <motion.div key={index} className="column is-4" variants={scaleIn}>
              <motion.div
                className="sds_eo-feature-card"
                whileHover={{
                  y: -8,
                  boxShadow: "0 15px 40px rgba(0,0,0,0.12)",
                }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <div
                  className="sds_eo-feature-icon"
                  style={{ background: feature.color }}
                >
                  {feature.icon}
                </div>
                <h3 className="title is-5 mt-4">{feature.title}</h3>
                <p className="has-text-grey">{feature.desc}</p>
              </motion.div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </AnimatedSection>
  );
};

// Benefits Section
const BenefitsSection: React.FC = () => {
  const benefits = [
    {
      icon: "⚡",
      title: "10x Faster Onboarding",
      desc: "Complete new hire paperwork in 30 minutes instead of 3 days",
      stats: "70% time reduction",
    },
    {
      icon: "💰",
      title: "Reduce Costs by 80%",
      desc: "Eliminate paper, printing, storage, and manual data entry costs",
      stats: "₹50,000+ annual savings",
    },
    {
      icon: "✅",
      title: "99% Accuracy",
      desc: "AI-powered extraction eliminates human data entry errors",
      stats: "Zero manual errors",
    },
    {
      icon: "😊",
      title: "Better Employee Experience",
      desc: "Give new hires a modern, professional onboarding experience",
      stats: "95% satisfaction rate",
    },
    {
      icon: "📊",
      title: "Complete Compliance",
      desc: "Automated compliance checks and complete audit trails",
      stats: "100% audit-ready",
    },
    {
      icon: "🌍",
      title: "Remote-Friendly",
      desc: "Onboard employees from anywhere in the world",
      stats: "Global accessibility",
    },
  ];

  return (
    <AnimatedSection className="sds_eo-benefits section sds_eo-bg-gradient">
      <div className="container">
        <div className="has-text-centered mb-6">
          <h2 className="title is-2 sds_eo-section-title has-text-white">
            Why HR Teams Love This Solution
          </h2>
          <p className="subtitle is-5 has-text-white-ter">
            Measurable results that transform your hiring process
          </p>
        </div>

        <motion.div
          className="columns is-multiline"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {benefits.map((benefit, index) => (
            <motion.div key={index} className="column is-4" variants={scaleIn}>
              <motion.div
                className="sds_eo-benefit-card"
                whileHover={{ y: -10 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <div className="sds_eo-benefit-icon">{benefit.icon}</div>
                <h3 className="title is-5 has-text-white">{benefit.title}</h3>
                <p className="has-text-white-ter mb-3">{benefit.desc}</p>
                <div className="sds_eo-stats-badge">{benefit.stats}</div>
              </motion.div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </AnimatedSection>
  );
};

// Security Section
const SecuritySection: React.FC = () => {
  const securityFeatures = [
    {
      icon: "🔐",
      title: "Bank-Grade Encryption",
      desc: "256-bit SSL encryption for all data transmission and storage",
    },
    {
      icon: "🛡️",
      title: "Data Privacy",
      desc: "Complete data privacy with role-based access controls",
    },
    {
      icon: "📜",
      title: "Audit Trails",
      desc: "Complete audit logs of all actions and document changes",
    },
    {
      icon: "✅",
      title: "Legal Compliance",
      desc: "Compliant with IT Act 2000, GDPR, and labor laws",
    },
    {
      icon: "☁️",
      title: "Secure Cloud Storage",
      desc: "Documents stored in secure, encrypted cloud infrastructure",
    },
    {
      icon: "🔒",
      title: "Access Controls",
      desc: "Multi-level permissions and authentication systems",
    },
  ];

  return (
    <AnimatedSection className="sds_eo-security section sds_eo-bg-light">
      <div className="container">
        <div className="has-text-centered mb-6">
          <h2 className="title is-2 sds_eo-section-title">
            🔒 Enterprise-Grade Security
          </h2>
          <p className="subtitle is-5">
            Your employee data is always safe with us
          </p>
        </div>

        <motion.div
          className="columns is-multiline"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {securityFeatures.map((feature, index) => (
            <motion.div key={index} className="column is-4" variants={fadeInUp}>
              <div className="sds_eo-security-card">
                <div className="sds_eo-security-icon">{feature.icon}</div>
                <h4 className="title is-5 has-text-centered">
                  {feature.title}
                </h4>
                <p className="has-text-centered has-text-grey">
                  {feature.desc}
                </p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </AnimatedSection>
  );
};

// CTA Section
const CTASection: React.FC = () => {
  return (
    <AnimatedSection className="sds_eo-cta section">
      <div className="container">
        <div className="box sds_eo-cta-box">
          <div className="has-text-centered">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <h2 className="title is-1 mb-5">
                Ready to Transform Your HR Onboarding?
              </h2>
              <p className="subtitle is-4 mb-6">
                Join hundreds of companies who have modernized their employee
                onboarding process
              </p>

              <div className="sds_eo-offer-badge mb-5">
                {/* <p className="is-size-3 has-text-weight-bold mb-2">🎁 Special Offer</p> */}
                <p className="is-size-4">First 2 Employee Onboardings FREE!</p>
                <p className="is-size-6 mt-3">
                  No credit card required • Setup in 30 minutes • Cancel anytime
                </p>
              </div>

              <div className="buttons is-centered are-large">
                <motion.a
                  href={`tel:${InfoService.mobile_no_1}`}
                  className="button is-primary is-large sds_eo-cta-button"
                  whileHover={{ scale: 1.05, y: -3 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <span className="icon">
                    <i className="fas fa-phone"></i>
                  </span>
                  <span>Call Now: {InfoService.mobile_no_1}</span>
                </motion.a>
                <motion.a
                  href={InfoService.loginLink}
                  className="button is-info is-large sds_eo-cta-button"
                  whileHover={{ scale: 1.05, y: -3 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <span className="icon">
                    <i className="fas fa-envelope"></i>
                  </span>
                  <span>Get Free Trial</span>
                </motion.a>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </AnimatedSection>
  );
};

// Main Component
const EmployeeOnboarding: React.FC = () => {
  return (
    <div className="sds_eo-wrapper">
      {/* SEO Meta Tags */}
      <div style={{ display: "none" }}>
        <h1>
          HDT Solutions - Employee Onboarding with AI & Digital Signatures
        </h1>
        <meta
          name="description"
          content="Streamline employee onboarding with AI-powered document extraction and digital signatures. Onboard new hires in minutes, not days. Secure, compliant, and paperless."
        />
        <meta
          name="keywords"
          content="employee onboarding, HR automation, digital signatures for HR, AI document extraction, paperless onboarding, HDT Solutions"
        />
      </div>

      <HeroSection />
      <HowItWorksSection />
      <KeyFeaturesSection />
      <BenefitsSection />
      <SecuritySection />
      <CTASection />

      <style>{`
        /* Base Styles */
        .sds_eo-wrapper {
          background: #ffffff;
          min-height: 100vh;
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
        }

        /* Brand Colors */
        :root {
          --primary-blue: #2196F3;
          --primary-purple: #7C4DFF;
          --dark-blue: #1e3c72;
          --success-green: #4CAF50;
        }

        /* Hero Section */
        .sds_eo-hero {
          background: linear-gradient(135deg, #E8EAF6 0%, #E1F5FE 100%);
          padding: 4rem 1.5rem;
        }

        .sds_eo-hero-title {
          color: var(--dark-blue);
          font-weight: 800;
          line-height: 1.2;
        }

        .sds_eo-hero-subtitle {
          color: var(--primary-purple);
          font-weight: 600;
        }

        .sds_eo-hero-description {
          color: #555;
          line-height: 1.8;
        }

        .sds_eo-feature-badges {
          display: flex;
          flex-wrap: wrap;
          gap: 0.75rem;
          margin-top: 2rem;
        }

        .sds_eo-badge {
          background: linear-gradient(135deg, var(--primary-blue), var(--primary-purple)) !important;
          color: white !important;
          font-weight: 600;
          padding: 0.75rem 1.5rem;
          border: none;
          box-shadow: 0 4px 15px rgba(124, 77, 255, 0.3);
        }

        .sds_eo-hero-visual {
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: 400px;
        }

        .sds_eo-animation {
          position: relative;
          width: 100%;
          height: 400px;
          display: flex;
          justify-content: center;
          align-items: center;
        }

        .sds_eo-icon-large {
          font-size: 150px;
          filter: drop-shadow(0 10px 30px rgba(0, 0, 0, 0.1));
        }

        .sds_eo-floating-elements {
          position: absolute;
          width: 100%;
          height: 100%;
        }

        .sds_eo-floating-element {
          position: absolute;
          font-size: 50px;
          filter: drop-shadow(0 5px 15px rgba(0, 0, 0, 0.1));
        }

        .sds_eo-floating-element:nth-child(1) { top: 10%; left: 15%; }
        .sds_eo-floating-element:nth-child(2) { top: 60%; left: 10%; }
        .sds_eo-floating-element:nth-child(3) { top: 20%; right: 15%; }
        .sds_eo-floating-element:nth-child(4) { bottom: 20%; right: 20%; }

        /* Section Title */
        .sds_eo-section-title {
          color: var(--dark-blue);
          font-weight: 700;
          position: relative;
          display: inline-block;
        }

        .sds_eo-section-title::after {
          content: '';
          display: block;
          width: 80px;
          height: 4px;
          background: linear-gradient(90deg, var(--primary-blue), var(--primary-purple));
          margin: 1rem auto 0;
          border-radius: 2px;
        }

        /* Background Gradients */
        .sds_eo-bg-gradient {
          background: linear-gradient(135deg, var(--primary-blue) 0%, var(--primary-purple) 100%);
          color: white;
          padding: 4rem 1.5rem;
        }

        .sds_eo-bg-light {
          background: linear-gradient(135deg, #E8EAF6 0%, #E1F5FE 100%);
          padding: 4rem 1.5rem;
        }

        /* Process Section */
        .sds_eo-process {
          padding: 4rem 1.5rem;
        }

        .sds_eo-process-card {
          background: white;
          padding: 2.5rem 2rem;
          border-radius: 20px;
          text-align: center;
          height: 100%;
          box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
          transition: all 0.3s ease;
          border-top: 4px solid var(--primary-purple);
        }

        .sds_eo-process-card:hover {
          box-shadow: 0 12px 35px rgba(124, 77, 255, 0.2);
        }

        .sds_eo-step-number {
          background: linear-gradient(135deg, var(--primary-purple), #5E35B1);
          color: white;
          width: 50px;
          height: 50px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
          margin: 0 auto 1.5rem;
          font-size: 1.75rem;
          box-shadow: 0 6px 20px rgba(124, 77, 255, 0.4);
        }

        .sds_eo-step-icon {
          font-size: 60px;
          margin-bottom: 1.5rem;
        }

        .sds_eo-feature-list {
          list-style: none;
          padding: 0;
          text-align: left;
        }

        .sds_eo-feature-list li {
          display: flex;
          align-items: center;
          margin-bottom: 0.5rem;
          color: #555;
        }

        .sds_eo-feature-list li .icon {
          margin-right: 0.5rem;
        }

        /* Features Section */
        .sds_eo-features {
          padding: 4rem 1.5rem;
        }

        .sds_eo-feature-card {
          background: white;
          border-radius: 20px;
          padding: 2.5rem 2rem;
          height: 100%;
          box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
          transition: all 0.3s ease;
          text-align: center;
        }

        .sds_eo-feature-icon {
          width: 80px;
          height: 80px;
          border-radius: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 40px;
          margin: 0 auto;
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
        }

        /* Benefits Section */
        .sds_eo-benefits {
          padding: 4rem 1.5rem;
        }

        .sds_eo-benefit-card {
          background: rgba(255, 255, 255, 0.1);
          border-radius: 20px;
          padding: 2.5rem 2rem;
          height: 100%;
          backdrop-filter: blur(10px);
          border: 2px solid rgba(255, 255, 255, 0.2);
          text-align: center;
          transition: all 0.3s ease;
        }

        .sds_eo-benefit-card:hover {
          background: rgba(255, 255, 255, 0.15);
          border-color: rgba(255, 255, 255, 0.4);
        }

        .sds_eo-benefit-icon {
          font-size: 60px;
          margin-bottom: 1rem;
        }

        .sds_eo-stats-badge {
          background: rgba(255, 255, 255, 0.9);
          color: var(--primary-purple);
          padding: 0.5rem 1rem;
          border-radius: 20px;
          font-weight: bold;
          display: inline-block;
          margin-top: 1rem;
        }

        /* Security Section */
        .sds_eo-security {
          padding: 4rem 1.5rem;
        }

        .sds_eo-security-card {
          background: white;
          padding: 2rem;
          border-radius: 15px;
          text-align: center;
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
          height: 100%;
        }

        .sds_eo-security-icon {
          font-size: 50px;
          margin-bottom: 1rem;
        }

        /* CTA Section */
        .sds_eo-cta {
          padding: 4rem 1.5rem;
          background: linear-gradient(135deg, #E8EAF6 0%, #E1F5FE 100%);
        }

        .sds_eo-cta-box {
          background: white;
          border-radius: 30px;
          padding: 4rem 2rem;
          box-shadow: 0 15px 50px rgba(0, 0, 0, 0.15);
        }

        .sds_eo-offer-badge {
          background: linear-gradient(135deg, #FFF9C4, #FFECB3);
          border: 3px solid #FFA726;
          border-radius: 20px;
          padding: 2rem;
          display: inline-block;
        }

        .sds_eo-cta-button {
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 1px;
          padding: 1.5rem 2.5rem !important;
          border-radius: 50px !important;
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
          transition: all 0.3s ease;
        }

        .sds_eo-cta-button:hover {
          box-shadow: 0 12px 35px rgba(0, 0, 0, 0.3);
        }

        /* Video Placeholder */
        .sds_eo-video-container {
          margin: 3rem 0;
          display: flex;
          justify-content: center;
        }

        .sds_eo-video-placeholder {
          background: linear-gradient(135deg, rgba(124, 77, 255, 0.1), rgba(33, 150, 243, 0.1));
          border: 3px dashed var(--primary-purple);
          border-radius: 20px;
          padding: 3rem 2rem;
          text-align: center;
          width: 100%;
          max-width: 800px;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .sds_eo-video-placeholder:hover {
          background: linear-gradient(135deg, rgba(124, 77, 255, 0.15), rgba(33, 150, 243, 0.15));
          border-color: var(--primary-blue);
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
        }

        .sds_eo-video-light {
          background: rgba(255, 255, 255, 0.9);
          border-color: rgba(255, 255, 255, 0.5);
        }

        .sds_eo-video-light:hover {
          background: white;
          border-color: white;
        }

        .sds_eo-video-placeholder .icon {
          color: var(--primary-purple);
          transition: all 0.3s ease;
        }

        .sds_eo-video-placeholder:hover .icon {
          color: var(--primary-blue);
          transform: scale(1.1);
        }

        /* Responsive Design */
        @media screen and (max-width: 768px) {
          .sds_eo-hero {
            padding: 2rem 1rem;
          }

          .sds_eo-hero-title {
            font-size: 2rem;
          }

          .sds_eo-icon-large {
            font-size: 100px;
          }

          .sds_eo-floating-element {
            font-size: 35px;
          }

          .sds_eo-section-title {
            font-size: 1.75rem;
          }

          .sds_eo-video-placeholder {
            padding: 2rem 1rem;
          }

          .sds_eo-cta-box {
            padding: 2rem 1rem;
          }

          .sds_eo-offer-badge {
            padding: 1.5rem;
          }

          .sds_eo-cta-button {
            padding: 1rem 1.5rem !important;
            font-size: 0.9rem;
          }
        }
      `}</style>
    </div>
  );
};

export default EmployeeOnboarding;
